import React, { useContext, useState } from 'react';
import { PageProps } from 'keycloakify/login/pages/PageProps';
import Mobile16 from '@bankiru/icons/react/Mobile16';
import Mobile24 from '@bankiru/icons/react/Mobile24';
import { Flexbox } from '@bankiru/ui-kit/components/Flexbox';
import Button from '@bankiru/ui-kit/core/Button';
import Text from '@bankiru/ui-kit/core/Text';

import ReachGoalOnMount from '../../../components/ReachGoalOnMount/ReachGoalOnMount';
import { EVENT_ACTIONS } from '../../../constants/yandexMetric';
import { ThemeContext } from '../../../context/ThemeContextWrapper';
import { saveConsents } from '../../../helpers/saveConsents';
import { useYandexMetric } from '../../../hooks/useYandexMetric';
import { I18n } from '../../i18n';
import { KcContext } from '../../KcContext';
import Template from '../../Template';
import { ConsentsList } from './components/ConsentsList/ConsentsList';
import { LoginSelectLayoutCommon } from './components/LoginSelectLayoutCommon/LoginSelectLayoutCommon';
import { MainSocialProvidersPanel } from './components/MainSocialProvidersPanel/MainSocialProvidersPanel';
import { OtherSocialProvidersPanel } from './components/OtherSocialProvidersPanel/OtherSocialProvidersPanel';
import { sortSocialProviders } from './helpers';
import { useLoginConsents } from './hooks';

const PAGE_ID = 'login-select.ftl';
type LoginSelectFormProps = PageProps<Extract<KcContext, { pageId: typeof PAGE_ID }>, I18n>;

export default function LoginSelectForm(props: LoginSelectFormProps) {
	const { i18n, kcContext, classes, doUseDefaultCss } = props;

	const { isAllRequiredChecked, onCheckboxChanged, checkedConsents } = useLoginConsents();

	const { msg, msgStr } = i18n;
	const { social, realm, registrationDisabled, url } = kcContext;

	const { trackYM } = useYandexMetric();

	const [isSubmitted, setIsSubmitted] = useState(false);

	const { isMobile, isIOS, platform, description } = useContext(ThemeContext);

	const isLoginDisabled = !isAllRequiredChecked || isSubmitted;

	const { mainSocialProviders, otherSocialProviders } = sortSocialProviders(
		social?.providers ?? [],
		isIOS,
		platform,
	);

	const onFormSubmit = () => {
		saveConsents(checkedConsents);
		setIsSubmitted(true);

		return true;
	};

	const renderConsents = () => (
		<ConsentsList checkedCheckboxes={checkedConsents} onCheckboxChanged={onCheckboxChanged} />
	);

	const renderPhoneButton = () => (
		<Button
			size="h48"
			skin="white"
			onClick={() => trackYM(EVENT_ACTIONS.CLICK_PHONE_AUTH_POPUP, platform)}
			leftIcon={{
				size24: <Mobile24 />,
				size16: <Mobile16 />,
			}}
			isDisabled={isLoginDisabled}
			type="submit"
			isFullWidth
		>
			Телефон
		</Button>
	);

	const renderMainProvidersList = () => (
		<MainSocialProvidersPanel
			providers={mainSocialProviders}
			checkedConsents={checkedConsents}
			isDisabled={isLoginDisabled}
		/>
	);

	const renderOtherProvidersList = () => (
		<OtherSocialProvidersPanel
			providers={otherSocialProviders}
			checkedConsents={checkedConsents}
			isDisabled={isLoginDisabled}
			i18n={i18n}
		/>
	);

	return (
		<Template
			kcContext={kcContext}
			i18n={i18n}
			doUseDefaultCss={doUseDefaultCss}
			classes={{
				...classes,
			}}
			headerNode={
				<Flexbox flexDirection="column" gap={isMobile ? '4' : '8'}>
					<Text
						size={isMobile ? 'fs18' : 'fs24'}
						color="primary"
						align="center"
						weight="bold"
					>
						{msgStr('loginHeader')}
					</Text>
					{description && (
						<Text
							size={isMobile ? 'fs14' : 'fs16'}
							color="secondary"
							align="center"
							weight="normal"
						>
							{description}
						</Text>
					)}
				</Flexbox>
			}
			displayInfo={realm.password && realm.registrationAllowed && !registrationDisabled}
			infoNode={
				<div id="kc-registration-container">
					<div id="kc-registration">
						<span>
							{msg('noAccount')}
							<a tabIndex={6} href={url.registrationUrl}>
								{msg('doRegister')}
							</a>
						</span>
					</div>
				</div>
			}
		>
			<ReachGoalOnMount
				goal={EVENT_ACTIONS.VIEW_AUTH_POPUP}
				eventLabel={`null|${window.location.pathname}|${platform}`}
			/>
			<div id="kc-form">
				<div id="kc-form-wrapper" className="kcFormWrapper">
					<form
						id="kc-form-login"
						onSubmit={onFormSubmit}
						action={url.loginAction}
						method="post"
					>
						<LoginSelectLayoutCommon
							phoneButton={renderPhoneButton()}
							consentsList={renderConsents()}
							mainProvidersList={renderMainProvidersList()}
							otherProvidersList={renderOtherProvidersList()}
							i18n={i18n}
						/>
					</form>
				</div>
			</div>
		</Template>
	);
}
